import { defineStore, storeToRefs } from 'pinia';
import { onBeforeMount, ref } from 'vue';

import type { FiltersStorage } from '@/services/localStorageManager';
import type { DiffusionConfig } from '@/types/configuration';
import type { Filters, Taxonomy, TaxonomyType } from '@/types/filters';
import type { BoundingBox } from '@/types/mode-map';

import { initialFilters } from '@/services/initialFilters';
import { LocalStorageManager } from '@/services/localStorageManager';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useProductsStore } from '@/stores/useProductsStore';

export const useFiltersStore = defineStore('filters', () => {
  /* VARIABLES */
  const storage: FiltersStorage = new LocalStorageManager();
  const filters = ref<Filters>(JSON.parse(JSON.stringify(initialFilters)));
  const datesAsStrings = ref<string | null>(null);

  /* STORE IMPORTS */
  const productsStore = useProductsStore();
  const globalConfigurationStore = useGlobalConfigurationStore();
  const { currentLocale } = storeToRefs(globalConfigurationStore);

  /* FILTERS STORAGE */
  function saveToStorage(filters: Filters) {
    storage.save(filters);
  }

  function loadFromStorage() {
    const savedFilters = storage.load();
    if (savedFilters) {
      filters.value = savedFilters;
    }
  }

  /* METHODS */
  function setListSelectionPreset(taxonomies: Array<{ type: TaxonomyType; value: string[] }>) {
    if (!taxonomies || taxonomies.length === 0)
      return;

    filters.value.metrics = filters.value.metrics.filter((it: any) => it.type !== 'crossedTaxonomyCount');
    filters.value.selections = [];

    for (const selection of taxonomies) {
      switch (selection.type) {
        case 'THEMES':
          filters.value.metrics.push({
            type: 'crossedTaxonomyCount',
            taxonomyTypes: ['TYPES'],
            crossedTaxonomies: { themes: [...selection.value] },
          });
          filters.value.selections.push({
            filterProperties: { type: 'taxonomyThemes' },
            selection: [...selection.value],
          });
          break;
        case 'TYPES':
          filters.value.selections.push({
            filterProperties: { type: 'taxonomyTypes' },
            selection: [...selection.value],
          });
          break;
        case 'AUDIENCES':
          filters.value.selections.push({
            filterProperties: { type: 'taxonomyAudiences' },
            selection: [...selection.value],
          });
          break;
        case 'PLACES':
          filters.value.selections.push({
            filterProperties: { type: 'taxonomyPlaces' },
            selection: [...selection.value],
          });
          break;
        case 'INTERNALS':
          filters.value.selections.push({
            filterProperties: { type: 'taxonomyInternals' },
            selection: [...selection.value],
          });
          break;
        default:
          console.error('Invalid taxonomy found in config');
      }
    }
    saveToStorage(filters.value);
  }

  function addSearchingFilter(newSearchFilter: string) {
    const existingIndex = filters.value.sorts.findIndex(it => it.type === 'search');
    if (!newSearchFilter.length) {
      if (existingIndex > -1)
        filters.value.sorts.splice(existingIndex, 1);
    }
    else {
      if (existingIndex > -1) {
        filters.value.sorts[existingIndex].query = newSearchFilter;
        filters.value.sorts[existingIndex].lang = currentLocale.value;
      }
      else {
        filters.value.sorts.push({ type: 'search', query: newSearchFilter, lang: currentLocale.value });
      }
    }
    saveToStorage(filters.value);
  }

  function addDatesFilter(dates: { start: number; end: number }) {
    setDatesAsStrings(dates);
    const existingDatesFiltersIndex = filters.value.filters?.findIndex(it => it.filterProperties.type === 'date_range');
    if (existingDatesFiltersIndex! > -1) {
      filters.value.filters?.splice(existingDatesFiltersIndex!, 1);
    }
    filters.value.filters?.push({
      filterProperties: { type: 'date_range' },
      selection: { endDate: dates.end, startDate: dates.start },
    });
    saveToStorage(filters.value);
    productsStore.fetchProductsList(filters.value)
      .catch((err: unknown) => console.error(err));
  }

  function setDatesAsStrings(dates: { start: number; end: number }) {
    const startDate = new Intl.DateTimeFormat(currentLocale.value, {
      day: 'numeric',
      month: 'short',
    }).format(dates.start);
    const altStartDate = new Intl.DateTimeFormat(currentLocale.value, { day: 'numeric' }).format(dates.start);
    const endDate = new Intl.DateTimeFormat(currentLocale.value, { day: 'numeric', month: 'short' }).format(dates.end);
    datesAsStrings.value = dates.start === dates.end ? startDate : `${altStartDate} - ${endDate}`;
  }

  function addSortingFilter(newSorting: string) {
    filters.value.sorts = filters.value.sorts.filter((it: any) => it.type === 'search');
    if (newSorting !== 'relevance') {
      filters.value.sorts.push({ type: newSorting });
    }
    saveToStorage(filters.value);
    productsStore.fetchProductsList(filters.value)
      .catch((err: unknown) => console.error(err));
  }

  function addPlacesFilters(newPlaces: any[]) {
    const taxonomyPlacesSelection = filters.value.selections.findIndex(it => it.filterProperties?.type === 'taxonomyPlaces');

    if (newPlaces.length === 0) {
      if (taxonomyPlacesSelection > -1) {
        filters.value.selections.splice(taxonomyPlacesSelection, 1);
      }
      saveToStorage(filters.value);
      productsStore.fetchProductsList(filters.value)
        .catch((err: unknown) => console.error(err));
      return;
    }

    if (taxonomyPlacesSelection > -1) {
      filters.value.selections[taxonomyPlacesSelection].selection = [];
      for (const place of newPlaces) {
        filters.value.selections[taxonomyPlacesSelection].selection.push(place.id);
      }
    }
    else {
      filters.value.selections.push({
        filterProperties: { type: 'taxonomyPlaces' },
        selection: newPlaces.map((it: any) => it.id),
      });
    }
    saveToStorage(filters.value);
    productsStore.fetchProductsList(filters.value)
      .catch((err: unknown) => console.error(err));
  }

  function addBoundingBoxFilter(boundingBox: BoundingBox) {
    const existingIndex = filters.value.filters.findIndex(it => it.filterProperties.type === 'geo_bounding_box');
    if (existingIndex > -1) {
      filters.value.filters.splice(existingIndex, 1);
    }
    filters.value.filters.push({
      filterProperties: { type: 'geo_bounding_box' },
      selection: boundingBox,
    });
    saveToStorage(filters.value);
  }

  function handleDistributorFilters(list: Taxonomy[], distributorId: string) {
    if (!distributorId)
      return console.error('distributorId is missing');
    const distributorFilterIndex = filters.value.selections.findIndex(it => it.filterProperties?.type === 'product_enrichment_taxonomy');

    if (list.length === 0) {
      if (distributorFilterIndex > -1) {
        filters.value.selections.splice(distributorFilterIndex, 1);
      }
      saveToStorage(filters.value);
      productsStore.fetchProductsList(filters.value)
        .catch((err: unknown) => console.error(err));
      return;
    }

    if (distributorFilterIndex > -1) {
      filters.value.selections[distributorFilterIndex].selection = [];
      for (const taxonomy of list) {
        filters.value.selections[distributorFilterIndex].selection.push(taxonomy.id);
      }
    }
    else {
      filters.value.selections.push({
        filterProperties: { type: 'product_enrichment_taxonomy', method: 'OR', distributor_id: distributorId },
        selection: list.map((it: Taxonomy) => it.id),
      });
    }
    saveToStorage(filters.value);
    productsStore.fetchProductsList(filters.value)
      .catch((err: unknown) => console.error(err));
  }

  function initializeHomeURL() {
    filters.value.selections = [];
    filters.value.metrics = filters.value.metrics.filter((it: any) => it.type !== 'crossedTaxonomyCount');
    filters.value.sorts = [{ type: 'random', seed: Date.now() }];
    const customFilterImported = filters.value.filters.find(it => it.filterProperties.type === 'custom_filter' && it.filterProperties.target === 'product' && it.filterProperties.field === 'imported_in_langs');
    if (!customFilterImported) {
      filters.value.filters.push({
        filterProperties: {
          type: 'custom_filter',
          target: 'product',
          field: 'imported_in_langs',
        },
        selection: currentLocale.value,
      });
    }
    saveToStorage(filters.value);
  }

  function setLocationFilters(config: DiffusionConfig) {
    if (!config.lat || !config.lon || !config.distance) {
      console.warn('geolocalisation data is missing');
      return;
    }
    filters.value.filters.push({
      filterProperties: { type: 'geo_distance' },
      selection: { lat: config.lat, lon: config.lon, distance: config.distance },
    });
    saveToStorage(filters.value);
  }

  function resetFilters() {
    filters.value = JSON.parse(JSON.stringify(initialFilters));
    addSearchingFilter('');
    datesAsStrings.value = null;
    saveToStorage(filters.value);
    productsStore.fetchProductsList(filters.value)
      .catch((err: unknown) => console.error(err));
  }

  function resetRdbkFilters() {
    window.location.reload();
  }

  onBeforeMount(() => {
    loadFromStorage();
  });

  return {
    filters,
    storage,
    datesAsStrings,
    saveToStorage,
    loadFromStorage,
    setListSelectionPreset,
    addSearchingFilter,
    addPlacesFilters,
    addDatesFilter,
    addSortingFilter,
    addBoundingBoxFilter,
    setDatesAsStrings,
    handleDistributorFilters,
    initializeHomeURL,
    resetRdbkFilters,
    setLocationFilters,
    resetFilters,
  };
});
